import { Component, ElementRef, EventEmitter, Input, OnInit, Output, QueryList, Signal, ViewChildren, inject } from '@angular/core';
import { AssignedDriver, AssignedVehicles, ModalConfig } from '../../models/vehicle-assignment.model';
import { AssignmentType } from '../../../../_shared/enums/assignment-type.enum';
import { TruncatetextPipe } from '../../../../_shared/pipes/truncatetext.pipe';
import { AvatarComponent } from '../../../../_shared/components/avatar/avatar.component';
import { NgbModal, NgbModalOptions, NgbTooltipModule } from '@ng-bootstrap/ng-bootstrap';
import { Router } from '@angular/router';
import { VehicleAssignmentModalComponent } from '../../../_shared/components/modals/driver-assignment/vehicle-assignment-modal.component';
import { UnifiedTreeItem } from '../../../../_shared/models/driver.model';
import { SingleAssignmentModalConfig, SingleUnAssignmentModalConfig } from '../../../../_shared/constants/assignment-modal-config.constants';
import { DriverAssignmentFilter } from '../../../../_shared/models';
import { AssignmentFilterService } from '../../services/assignment-filter.service';
import { Subject } from 'rxjs';
import { TranslocoDirective, TranslocoPipe, TranslocoService } from '@jsverse/transloco';

@Component({
  selector: 'app-driver-vehicle-assignment-row',
  standalone: true,
  imports: [TruncatetextPipe, AvatarComponent, NgbTooltipModule, TranslocoDirective, TranslocoPipe],
  templateUrl: './driver-vehicle-assignment-row.component.html',
  styleUrl: './driver-vehicle-assignment-row.component.scss'
})
export class DriverVehicleAssignmentRowComponent implements OnInit {

  @ViewChildren('assignedVehicleTagsScrollArea') assignedVehicleTagsScrollAreas: QueryList<ElementRef>;
  @ViewChildren('pendingVehicleTagsScrollArea') pendingVehicleTagsScrollAreas: QueryList<ElementRef>;
  @ViewChildren('pendingUnassignmentTagsScrollArea') pendingUnassignmentTagsScrollAreas: QueryList<ElementRef>;

  @Input() driver: AssignedDriver;
  @Input() billingAccountId: number;
  @Input() hasFleetManagerRole: boolean = false;
  @Input() assetsLoaded: Signal<boolean>;
  @Input() hierarchicalAssetsTree: UnifiedTreeItem[] = [];

  @Output() assignmentUpdated = new EventEmitter<void>();

  public assignedVehicles: AssignedVehicles[] = [];
  public pendingAssignmentVehicles: AssignedVehicles[] = [];
  public pendingUnassignmentVehicles: AssignedVehicles[] = [];

  private cancelSubject: Subject<void> = new Subject<void>();
  private readonly transloco = inject(TranslocoService);

  constructor(
    private router: Router,
    private modalService: NgbModal,
    private assignmentFilterService: AssignmentFilterService) { }

  ngOnInit(): void {
    this.handleAssignmentMapping();
  }

  ngOnDestroy(): void {
    this.cancelSubject.next();
    this.cancelSubject.complete();
  }

  getDriverName(firstName: string, lastName: string): string {
    return `${firstName ?? ''} ${lastName ?? ''}`.trim();
  }

  handleShowAll(driver: any, type: string) {
    const driverId = driver.driverId;
    const fullName = `${driver.firstName} ${driver.lastName}`;
    const hasFleetManagerRole = this.hasFleetManagerRole;
    const isDriverDeleted = driver.deleted;

    this.assignmentFilterService.selectedFilterType.set(type);
    this.router.navigate(['/driver-management/driver-vehicle-assignment-list'], { state: { fullName, driverId, hasFleetManagerRole, isDriverDeleted } });
  }

  handleAssignmentMapping() {
    this.assignedVehicles = this.driver.vehicleAssignments.filter(x => x.isSynced && x.assignmentTypeId == AssignmentType.Assign)
    this.pendingAssignmentVehicles = this.driver.vehicleAssignments.filter(x => !x.isSynced && x.assignmentTypeId == AssignmentType.Assign)
    this.pendingUnassignmentVehicles = this.driver.vehicleAssignments.filter(x => !x.isSynced && x.assignmentTypeId == AssignmentType.Unassign)
  }

  scrollVehicleTags(scrollType: 'assigned' | 'pending' | 'pending-unassign', driverId: number, direction: 'left' | 'right') {
    let scrollAreaList: QueryList<ElementRef>;

    switch (scrollType) {
      case 'assigned':
        scrollAreaList = this.assignedVehicleTagsScrollAreas;
        break;
      case 'pending':
        scrollAreaList = this.pendingVehicleTagsScrollAreas;
        break;
      case 'pending-unassign':
        scrollAreaList = this.pendingUnassignmentTagsScrollAreas;
        break;
      default:
        return;
    }

    const scrollArea = scrollAreaList.find(
      area => area.nativeElement.id === `${scrollType}-${driverId}`
    );

    if (scrollArea) {
      const element = scrollArea.nativeElement;
      const scrollValue = direction === 'left' ? 0 : element.scrollWidth;
      element.scrollTo({ left: scrollValue, behavior: 'smooth' });
    }
  }

  handleSingleUnassignModal(driver: any, vehicle: any): void {
    const config = SingleUnAssignmentModalConfig;

    const modalConfig: ModalConfig = {
      ...config,
      title: this.transloco.translate('vehicleAssignment.modals.unassignDriver.title'),
      confirmationMessage: this.transloco.translate('vehicleAssignment.modals.unassignDriver.message', { driver: `${driver.firstName} ${driver.lastName}`, vehicle: vehicle.assetName }),
      warningMessage: this.transloco.translate('vehicleAssignment.modals.unassignDriver.warning'),
      assignmentFilter: {
        assets: [{
          id: vehicle.assetId,
          name: vehicle.assetName
        }],
        drivers: [{
          id: driver.driverId,
          name: `${driver.firstName} ${driver.lastName}`
        }]
      } as DriverAssignmentFilter

    };

    this.openVehicleAssignmentModal(modalConfig);
  }

  handleSingleAssignModal(driver: AssignedDriver): void {
    const config = SingleAssignmentModalConfig;

    const modalConfig: ModalConfig = {
      ...config,
      title: this.transloco.translate('vehicleAssignment.modals.assignDriver.title'),
      confirmationButtonText: this.transloco.translate('common.assign'),
      cancelButtonText: this.transloco.translate('common.cancel'),
      hierarchicalAssetsTree: this.hierarchicalAssetsTree,
      assetsLoaded: this.assetsLoaded,
      assignmentFilter: {
        drivers: [{
          id: driver.driverId
        }]
      } as DriverAssignmentFilter
    };

    this.assignmentFilterService.setSelectedDrivers([{ id: driver.driverId, name: `${driver.firstName} ${driver.lastName}` }]);
    this.assignmentFilterService.setAssignmentBillingAccountId(driver.billingAccountId);
    this.assignmentFilterService.updateAssignedAssetDrivers(this.cancelSubject);

    this.openVehicleAssignmentModal(modalConfig)
  }

  cancelPendingSingleUnassignModal(driver: any, vehicle: any) {
    const config = SingleUnAssignmentModalConfig;

    const modalConfig: ModalConfig = {
      ...config,
      title: this.transloco.translate('vehicleAssignment.modals.cancelPendingAssignment.title'),
      cancelButtonText: this.transloco.translate('vehicleAssignment.modals.noDontCancel'),
      confirmationButtonText: this.transloco.translate('vehicleAssignment.modals.yesCancel'),
      confirmPlaceholder: this.transloco.translate('vehicleAssignment.modals.typeCancelToConfirm'),
      confirmationPrompt: this.transloco.translate('vehicleAssignment.modals.cancelPrompt'),
      warningMessage: this.transloco.translate('vehicleAssignment.modals.cancelPendingAssignment.warning'),
      confirmationMessage: this.transloco.translate('vehicleAssignment.modals.cancelPendingAssignment.message', { driver: `${driver.firstName} ${driver.lastName}`, vehicle: vehicle.assetName }),
      assignmentFilter: {
        assets: [{
          id: vehicle.assetId,
          name: vehicle.assetName
        }],
        drivers: [{
          id: driver.driverId,
          name: `${driver.firstName} ${driver.lastName}`
        }]
      } as DriverAssignmentFilter

    };
    this.openVehicleAssignmentModal(modalConfig);
  }

  handleCancelUnAssignment(driver: AssignedDriver, vehicle: AssignedVehicles) {
    const config = SingleAssignmentModalConfig;

    const modalConfig: ModalConfig = {
      ...config,
      showAssetFilter: false,
      showDrivers: false,
      showAssets: false,
      showConfirmationMessage: true,
      showConfirmationWarning: true,
      title: this.transloco.translate('vehicleAssignment.modals.cancelPendingUnassignment.title'),
      confirmationButtonText: this.transloco.translate('vehicleAssignment.modals.yesCancel'),
      confirmPlaceholder: this.transloco.translate('vehicleAssignment.modals.typeCancelToConfirm'),
      confirmationPrompt: this.transloco.translate('vehicleAssignment.modals.cancelPrompt'),
      warningMessage: this.transloco.translate('vehicleAssignment.modals.cancelPendingUnassignment.warning'),
      confirmationMessage: this.transloco.translate('vehicleAssignment.modals.cancelPendingUnassignment.message', { driver: `${driver.firstName} ${driver.lastName}`, vehicle: vehicle.assetName }),
      isDriverDeleted: driver.deleted,
      assignmentFilter: {
        assets: [{
          id: vehicle.assetId,
          name: vehicle.assetName
        }],
        drivers: [{
          id: driver.driverId,
          name: `${driver.firstName} ${driver.lastName}`
        }]
      } as DriverAssignmentFilter
    }

    this.openVehicleAssignmentModal(modalConfig)
  }

  private openVehicleAssignmentModal(config: ModalConfig,): void {
    const modalOptions: NgbModalOptions = { centered: true, backdrop: 'static', size: 'xl' };
    const modalRef = this.modalService.open(VehicleAssignmentModalComponent, modalOptions);

    modalRef.componentInstance.config = config;

    modalRef.closed.subscribe(() => {
      this.refreshList();
      this.assignmentFilterService.isSingleAssignment.set(false);
    });
  }

  refreshList(): void {
    this.assignmentUpdated.emit();
  }
}